"use client";
import type { ReactNode } from 'react';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect } from 'react';

import { MATHJAX_CONFIG } from './config';
import {
  useEventManager,
  useGlobalRerenderMath,
  useMathJaxRenderer,
  useMathJaxScript,
} from './hooks';

interface MathJaxProviderProps {
  children: ReactNode;
}

const MathJaxProvider: React.FC<MathJaxProviderProps> = ({ children }) => {
  const pathname = usePathname();
  const { addManagedEventListener } = useEventManager();
  const { activeOperations, rerenderMath } = useMathJaxRenderer();
  const { handleScriptError, handleScriptLoad, isInitialized } = useMathJaxScript(
    rerenderMath,
    addManagedEventListener
  );

  // Trigger rerender on pathname changes
  useEffect(() => {
    if (!pathname) return;
    const id = setTimeout(() => {
      rerenderMath().catch(() => {});
    }, 500);
    return () => clearTimeout(id);
  }, [pathname, rerenderMath]);

  useEffect(() => {
    if (window.MathJax && window.MathJax.typesetPromise) {
      if (!isInitialized.current) {
        isInitialized.current = true;
        window.__MATHJAX_READY__ = true;
        setTimeout(() => rerenderMath(), 100);
      }
      return;
    }
    // @ts-ignore - MathJax global config
    window.MathJax = MATHJAX_CONFIG;
  }, [rerenderMath, isInitialized]);

  useGlobalRerenderMath(rerenderMath);

  useEffect(() => {
    const currentActiveOperations = activeOperations.current;

    return () => {
      currentActiveOperations.clear();
    };
  }, [activeOperations]);

  return (
    <>
      <Script
        id="MathJax-script"
        onError={handleScriptError}
        onLoad={handleScriptLoad}
        src="https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js"
        strategy="afterInteractive"
      />
      {children}
    </>
  );
};

export default MathJaxProvider;